import { Autocomplete, Box, Checkbox, FormControl, FormControlLabel, FormHelperText, InputLabel, Popper, Select, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useState } from "react";
import { MinusIcon, PlusIcon } from "../icons/Icons";
import { validationUtils, useInputValidators } from "./useInputValidators";
import EditableTypography from "./EditableTypograpy";
import PropTypes from 'prop-types';
import { dateUtils } from "../../utils/DateUtils";
import LabelledBox from "./LabelledBox";
import ImageFileInput from "./ImageFileInput";

/**
 * validated Higher-Order Component (HOC)
 *
 * This higher-order component (HOC) is designed to enhance the functionality of input components
 * by adding validation capabilities and error handling. It can be used with various input components
 * to provide a consistent way of validating user input and communicating validation results.
 *
 * @param {React.Component} InputComponent - The input component to be enhanced with validation features.
 * It should accept certain props for validation, such as 'id', 'onChange', 'value', 'getErrorMessage', and
 * 'disableErrorOnFirstRender'.
 *
 * @returns {React.Component} - A new input component with added validation features, including error
 * validation and communication with the parent component.
 */

const isEmpty = (value) =>
    value === "" ||
    value == null ||
    value === undefined ||
    (typeof value === "number" && Number.isNaN(value));

export const validated = (InputComponent, defaultValidators) => ({ ...props }) => {
    const { id, onChange, value, onBlur } = props;
    const { getErrorMessage, disableError, email, phoneNumber, ...rest } = props;

    const [touched, setTouched] = useState(false);
    const validators = validationUtils.extractInputValidatorsFromProps({ ...defaultValidators, ...props });
    const [validationResult, validate] = useInputValidators(validators, getErrorMessage);

    const validatedOnChange = (newValue) => {
        setTouched(true);

        const _validationResult = validate(newValue);

        onChange && onChange(id, newValue, _validationResult?.valid);
    };

    const validatedOnBlur = (evt) => {
        setTouched(true);
        onBlur?.(evt);
    };

    const restWithoutBlur = { ...rest };
    delete restWithoutBlur.onBlur;

    //validate on every render
    useEffect(() => {
        validate(value);
    });

    //communicate out every time validation result is changed
    useEffect(() => {
        onChange && onChange(id, value, validationResult.valid);
    }, [validationResult]);// eslint-disable-line react-hooks/exhaustive-deps

    const suppressErrorUntilTouched = !touched && isEmpty(value);
    const showAsValid = disableError || suppressErrorUntilTouched || validationResult.valid;
    const displayErrorMessage = showAsValid ? null : (validationResult.message || validationResult.reason);

    return (
        <InputComponent
            {...restWithoutBlur}
            onChange={validatedOnChange}
            onBlur={validatedOnBlur}
            valid={showAsValid}
            errorMessage={displayErrorMessage}
        />
    );
};

/**
 * ExtendedTextField Component
 *
 * This component extends the functionality of the TextField component from a UI library
 * by providing additional props to control error validation and helper text display.
 *
 * @param {Function} onChange - A callback function to handle changes in the input value.
 * @param {boolean} valid - A boolean value indicating whether the input is valid or not.
 * @param {string} errorMessage - The error message to display when 'valid' is false.
 * @param {boolean} disableHelperText - Set to true to disable the helper text display.
 * @param {any} rest - Additional props to pass to the underlying TextField component.
 *
 * @returns {JSX.Element} - A TextField component with extended features.
 */
export const ExtendedTextField = ({ onChange, valid, errorMessage, disableHelperText, ...rest }) => {
    const _onChange = (evt) => {
        onChange && onChange(evt.target.value);
    }

    return <TextField {...rest} onChange={_onChange} helperText={!disableHelperText && (valid ? ' ' : errorMessage)} error={!valid} />
}

const roundToDecimals = (n, d) => {
    if (n == null || (typeof n === "number" && Number.isNaN(n))) {
        return Number.NaN;
    }
    const num = typeof n === "string" ? parseFloat(n) : n;
    if (Number.isNaN(num)) {
        return Number.NaN;
    }
    const f = 10 ** d;
    return Math.round(num * f) / f;
};

const formatNumberDisplay = (value, decimals) => {
    if (value == null || value === "" || (typeof value === "number" && Number.isNaN(value))) {
        return "";
    }
    const n = typeof value === "string" ? parseFloat(value) : value;
    if (Number.isNaN(n)) {
        return "";
    }
    return roundToDecimals(n, decimals).toFixed(decimals);
};

const parseInputToNumber = (raw) => {
    if (raw == null) {
        return Number.NaN;
    }
    const t = String(raw).trim();
    if (t === "" || t === "-") {
        return Number.NaN;
    }
    const n = parseFloat(t);
    return Number.isNaN(n) ? Number.NaN : n;
};

/**
 * NumberInput Component
 *
 * This is a customizable, controlled React component for numeric input fields.
 * It encapsulates an input field along with optional increment and decrement buttons.
 *
 * @param {Number|string} value - The current value of the input field.
 * @param {Function} onChange - Function to be called when the value changes. Receives a number (or NaN when the field is empty/invalid).
 * @param {Number} [min] - The minimum allowed value for the input field.
 * @param {Number} [max] - The maximum allowed value for the input field.
 * @param {Boolean} [disabled] - A flag to specify whether the input field is disabled or not.
 * @param {Boolean} [hideButtons] - A flag to determine if the increment and decrement buttons should be hidden.
 * @param {Number} [decimals=2] - How many fraction digits to show and to round to on change.
 * @param {any} rest - Other props to be passed to the ExtendedTextField component.
 *
 * @returns {JSX.Element} NumberInput component with an input field and, optionally, increment and decrement buttons.
 */
export const NumberInput = (props) => {
    const {
        value,
        onChange,
        min,
        max,
        disabled,
        label,
        hideButtons,
        decimals = 2,
        onFocus: userOnFocus,
        onBlur: userOnBlur,
        ...rest
    } = props;

    const [focused, setFocused] = useState(false);
    const [draft, setDraft] = useState("");

    const nValue = (() => {
        if (value == null || value === "" || (typeof value === "number" && Number.isNaN(value))) {
            return Number.NaN;
        }
        if (typeof value === "string") {
            return parseInputToNumber(value);
        }
        return typeof value === "number" ? value : parseInputToNumber(String(value));
    })();

    const displayValue = focused ? draft : formatNumberDisplay(nValue, decimals);

    const handleChange = (payload) => {
        // ExtendedTextField calls `onChange(evt.target.value)` (string), not the raw event
        const raw =
            payload != null && (typeof payload === "string" || typeof payload === "number")
                ? String(payload)
                : payload?.target != null
                  ? String(payload.target.value)
                  : "";

        if (!focused) {
            setFocused(true);
        }
        setDraft(raw);
        if (onChange) {
            if (raw.trim() === "" || raw === "-" || raw === ".") {
                onChange(Number.NaN);
            } else {
                const n = parseInputToNumber(raw);
                if (Number.isNaN(n)) {
                    onChange(Number.NaN);
                } else {
                    onChange(roundToDecimals(n, decimals));
                }
            }
        }
    };

    const handleFocus = (evt) => {
        setFocused(true);
        setDraft(formatNumberDisplay(nValue, decimals));
        userOnFocus?.(evt);
    };

    const handleBlur = (evt) => {
        const raw = evt.target.value;
        setFocused(false);
        setDraft("");
        if (onChange) {
            const n = parseInputToNumber(raw);
            if (Number.isNaN(n)) {
                onChange(Number.NaN);
            } else {
                onChange(roundToDecimals(n, decimals));
            }
        }
        userOnBlur?.(evt);
    };

    const onKeyDown = (e) => {
        if (e.ctrlKey || e.metaKey || e.altKey) {
            return;
        }
        if (e.key === "." && decimals === 0) {
            e.preventDefault();
            return;
        }
        if (
            !/[0-9.-]/.test(e.key) &&
            e.key !== "Backspace" &&
            e.key !== "Delete" &&
            e.key !== "ArrowLeft" &&
            e.key !== "ArrowRight" &&
            e.key !== "Tab" &&
            e.key !== "Home" &&
            e.key !== "End"
        ) {
            e.preventDefault();
        }
    };

    const atMax = max != null && !Number.isNaN(nValue) && nValue >= max;
    const atMin = min != null && !Number.isNaN(nValue) && nValue <= min;

    return <Box sx={{ display: "flex", flexDirection: "row", gap: "10px", justifyContent: "center" }}>
        <ExtendedTextField
            {...rest}
            onChange={handleChange}
            value={displayValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            autoComplete={"off"}
            label={label}
        />
        {!hideButtons && <Box sx={{ display: "flex", height: "40px", alignItems: "center", marginTop: label ? "8px" : "0px" }}>
            <PlusIcon
                onClick={() => {
                    const start = Number.isNaN(nValue) ? (min != null && min > 0 ? min : 0) : nValue;
                    onChange && onChange(roundToDecimals(start + 1, decimals));
                }}
                size="small"
                disabled={disabled || atMax}
                data-testid="increment-button"
            />
            <MinusIcon
                onClick={() => {
                    const start = Number.isNaN(nValue) ? (max != null && max < 0 ? max : 0) : nValue;
                    onChange && onChange(roundToDecimals(start - 1, decimals));
                }}
                size="small"
                disabled={disabled || atMin}
                data-testid="decrement-button"
            />
        </Box>}
    </Box>
}

/**
 * DateInput Component
 *
 * This component provides a DatePicker interface. It uses the UI library's DatePicker and LocalizationProvider.
 * It also uses a custom 'ExtendedTextField' component to display DatePicker input field.
 *
 * @param {Object} props - The props for this component. *
 * @property {Date|string} value - The currently selected date.
 * @property {Function} onChange - A callback function to handle changes in the date selection.
 * @property {Object} textFieldProps - Additional props to pass to the underlying ExtendedTextField component used in DatePicker.
 *
 * @returns {JSX.Element} - Wraps the DatePicker component in LocalizationProvider. The DatePicker uses the ExtendedTextField as the input renderer.
 */
export const DateInput = (props) => {
    const { value, onChange, textFieldProps, valid = true, errorMessage, datePickerProps, ...rest } = props;
    const { required } = rest;

    return <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
            {...rest}
            {...datePickerProps}
            value={value}
            renderInput={(params) => <ExtendedTextField {...params} required={required} valid={valid} errorMessage={errorMessage} {...textFieldProps} />}
            onChange={onChange}
            slotProps={{
                field: { clearable: true },
            }}
        />
    </LocalizationProvider>
}

const CountryFlagImg = ({ code, size = 20 }) => (
    code ? (
        <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', mr: 1, flexShrink: 0, marginLeft: '10px' }}
        >
            <img
                loading="lazy"
                width={size}
                height={Math.round(size * 0.7)}
                src={`https://flagcdn.com/w${size}/${(code || '').toLowerCase()}.png`}
                srcSet={`https://flagcdn.com/w${size * 2}/${(code || '').toLowerCase()}.png 2x`}
                alt=""
                style={{ display: 'block' }}
            />
        </Box>
    ) : null
);

export const Dropdown = ({ value, onChange, options, valid, errorMessage, textFieldProps, inputProps, required, inputStartAdornment, ...props }) => {
    return <Autocomplete
        value={value}
        onChange={(event, newValue) => onChange(newValue)}
        options={options}
        autoHighlight
        getOptionLabel={(option) => option.name}
        renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
                <Box
                    key={key}
                    component="li"
                    {...optionProps}
                >
                    {option.name}
                </Box>
            );
        }}
        renderInput={(params) => (
            <ExtendedTextField
                {...params}
                inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password', // disable autocomplete and autofill
                    ...inputProps,
                }}
                InputProps={{
                    ...params.InputProps,
                    ...(inputStartAdornment != null && {
                        startAdornment: (
                            <>
                                {inputStartAdornment}
                                {params.InputProps?.startAdornment}
                            </>
                        ),
                    }),
                }}
                valid={valid}
                errorMessage={errorMessage}
                required={required}
                {...textFieldProps}
            />
        )}
        {...props}
    />
}

export const DropdownSelector = ({ id, children, value, onChange, disabled, sx, label, ...props }) => {
    return <FormControl sx={{ margin: '8px 0px', ...sx }} {...props}>
        {label && <InputLabel>{label}</InputLabel>}
        <Select
            size='small'
            value={value}
            label={label}
            onChange={(evt) => onChange(evt.target.value)}
            disabled={disabled}
        >
            {children}
        </Select>
    </FormControl>
}

export const CountryDropdown = ({ value, onChange, countries = [], getOptionText, ...props }) => {
    return <ValidatedDropdown
        value={value}
        onChange={onChange}
        options={countries}
        disableClearable
        inputStartAdornment={value ? <CountryFlagImg code={value.code} /> : null}
        renderOption={(props, option) => {
            return (
                <Box
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...props}
                    key={option.code}
                >
                    <CountryFlagImg code={option.code} size={20} />
                    {getOptionText ? getOptionText(option) : option.name}
                </Box>
            );
        }}
        PopperComponent={CountryDropdownPopper}
        {...props}
    />
}

export const CountryDropdownPopper = (props) => {
    const { anchorEl } = props;

    return <Popper
        {...props}
        sx={{
            width: "auto !important",
            minWidth: anchorEl.offsetWidth,
        }}
    />
}

export const DayOfWeekSelector = ({
    id,
    label,
    value = [],
    onChange,
    onBlur,
    disabled,
    sx,
    valid = true,
    errorMessage,
    disableHelperText,
}) => {
    const locale = navigator.language;
    const daysArray = [1, 2, 3, 4, 5, 6, 7];
    const selectedDays = value ?? [];

    const onSelectDay = (selectedDay, selected) => {
        let newValue = [...selectedDays];

        if (selected) {
            newValue.push(selectedDay);
        } else {
            newValue = newValue.filter(day => day !== selectedDay);
        }

        newValue.sort();
        onChange?.(newValue);
        onBlur?.();
    };

    return (
        <Box sx={{ width: 'fit-content' }}>
            <LabelledBox
                label={label}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    borderColor: (theme) => (valid ? 'rgba(0, 0, 0, 0.23)' : theme.palette.error.main),
                    '& > span': {
                        color: (theme) => (valid ? 'rgba(0, 0, 0, 0.4)' : theme.palette.error.main),
                    },
                    ...sx,
                }}
            >
                {daysArray.map(day => (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedDays.includes(day)}
                                onChange={(evt) => onSelectDay(day, evt.target.checked)}
                                disabled={disabled}
                                color={valid ? 'primary' : 'error'}
                            />
                        }
                        label={dateUtils.getLocalizedDayName(day, locale)}
                        key={day}
                        labelPlacement="top"
                        sx={{ margin: '0px 5px' }}
                    />
                ))}
            </LabelledBox>
            {!disableHelperText && (
                <FormHelperText error={!valid} sx={{ mx: '14px', mt: 0.5, minHeight: '20px' }}>
                    {valid ? '\u00a0' : errorMessage}
                </FormHelperText>
            )}
        </Box>
    );
};

/**
 * ValidatedTextInput it a wrapped MUI TextField with validation capabilities provided by validated HOC
 */
export const ValidatedTextInput = validated(ExtendedTextField);
export const ValidatedNumberInput = validated(NumberInput, { numeric: true });
export const ValidatedDateInput = validated(DateInput);
export const ValidatedEditableTypography = validated(EditableTypography);
export const ValidatedDropdown = validated(Dropdown);
export const ValidatedImageFileInput = validated(ImageFileInput);
export const ValidatedDayOfWeekSelector = validated(DayOfWeekSelector);

ValidatedTextInput.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
    disableErrorOnFirstRender: PropTypes.bool,
    disableHelperText: PropTypes.bool,
    required: PropTypes.bool,
    unique: PropTypes.object,
    getErrorMessage: PropTypes.func,
};

ValidatedNumberInput.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    min: PropTypes.number,
    max: PropTypes.number,
    decimals: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    getErrorMessage: PropTypes.func,
    hideButtons: PropTypes.bool,
    disabled: PropTypes.bool,
    disableErrorOnFirstRender: PropTypes.bool,
};

ValidatedDateInput.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    onChange: PropTypes.func.isRequired,
    textFieldProps: PropTypes.object,
    getErrorMessage: PropTypes.func,
    disableErrorOnFirstRender: PropTypes.bool,
};

ValidatedDayOfWeekSelector.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(PropTypes.number),
    onChange: PropTypes.func,
    label: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    disableHelperText: PropTypes.bool,
    disableError: PropTypes.bool,
    getErrorMessage: PropTypes.func,
};
